/**
 * «Это не культура» — уточнение, ЧТО именно означает термин хозяйства.
 *
 * Раньше кнопка просто гасила строку без подтипа, и «Пар», «Сенокос» и
 * «Травосмесь» становились неразличимы. Теперь два шага:
 *   1. что это — состояние поля / тип угодья / смесь культур;
 *   2. конкретное значение (для смеси второй шаг не нужен).
 *
 * UAT founder на проде (v1.1746, дословно): «там есть пара культур вообще не
 * культур — их надо куда-то убрать, а в меню этой функции нет». Функция была
 * только в разборе порций; теперь тот же диалог открывается из карточки записи
 * справочника — и потому обязан говорить ПОСЛЕДСТВИЕ цифрами: за записью
 * «Услуги по выращ. семян сои» на проде стоят 100 посевов, и молча менять
 * смысл сотни посевов нельзя.
 *
 * DS: только @/components/ui/*, semantic Tailwind, без hex.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { ApiError } from '@/lib/api';
import {
  useCropRecordUsage,
  useMarkNotACrop,
  type CropQueueItem,
  type NotACropKind,
  type NotACropPayload,
} from '@/lib/reference/tenantCrops';
import {
  needsCatalogCode,
  NOT_A_CROP_KIND_LABEL,
  type CodeOption,
} from '@/lib/reference/notACropKinds';
import {
  useFieldStates,
  useLandCategories,
} from '@/lib/reference/fieldCatalogs';
import { Skeleton } from '@/components/ui/skeleton';
import { plural } from './catalog-review/reviewLabels';

const KIND_TILES: ReadonlyArray<{
  kind: NotACropKind;
  title: string;
  hint: string;
}> = [
  {
    kind: 'field_state',
    title: NOT_A_CROP_KIND_LABEL.field_state,
    hint: 'Пар, АМП, под мелиорацией',
  },
  {
    kind: 'land_parcel',
    title: NOT_A_CROP_KIND_LABEL.land_parcel,
    hint: 'Залежь, сенокос, пастбище, сад',
  },
  {
    kind: 'crop_mix',
    title: NOT_A_CROP_KIND_LABEL.crop_mix,
    hint: 'Травосмесь, несколько культур вместе',
  },
];

function errMsg(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.detail : fallback;
}

/** «1 посев» / «2 посева» / «5 посевов» — склонение общим `plural` каталога. */
function sowingsCount(n: number): string {
  return `${n} ${plural(n, 'посев', 'посева', 'посевов')}`;
}

/**
 * Граница действия — ОДНА строка на все три вида, и она обязана быть на экране.
 *
 * Ревью head-web (#854): «UI обещает шире, чем функция делает». Пометку читает
 * только легенда карты (`services/crop_legend.py` + `api/v1/scope.py`);
 * `dashboard.py::rotation_summary`, `crop_structure.py` (СПП) и
 * `analytics_history.py` группируют по `sowings.crop_id` и её НЕ видят.
 * Практический исход без этой строки: человек помечает «Услуги по выращ. семян
 * сои» (100 посевов, в 2026-м — 369 га), открывает СПП, видит те же 369 га сои
 * и делает единственный доступный вывод — «кнопка не работает».
 *
 * Строку сторожит тест: убрать её вместе с очередным рефактором текста нельзя.
 * Снимать — вместе с AK-712 (каскад пометки на сводки), не раньше.
 */
export const SUMMARIES_LIMIT_TEXT =
  'Пока это меняет только легенду карты. В сводках — СПП, дашборд, аналитика — ' +
  'запись продолжит учитываться как культура: их пересчёт идёт отдельной задачей.';

/**
 * Что именно произойдёт — текстом, который человек может проверить.
 *
 * Обещаем ровно то, что делает бэкенд: меняется КЛАССИФИКАЦИЯ САМОЙ ЗАПИСИ.
 * Формулировка «изменится то, чем система их считает» (где «их» = посевы) была
 * неправдой: у посевов `crop_id` остаётся на месте, и ни одна сводка по ним
 * ничего не пересчитывает. Границу договаривает :data:`SUMMARIES_LIMIT_TEXT`.
 */
export function consequenceText(
  kind: NotACropKind,
  valueLabel: string | null,
  sowings: number,
): string {
  const head =
    sowings > 0
      ? `${sowingsCount(sowings)} останутся на месте — ничего не удаляется и никуда не переносится. Изменится классификация самой записи: `
      : 'Посевов за записью нет. Изменится классификация самой записи: ';
  if (kind === 'field_state') {
    return `${head}это перестанет быть культурой и станет занятием поля${
      valueLabel ? ` «${valueLabel}»` : ''
    }.`;
  }
  if (kind === 'land_parcel') {
    return `${head}это перестанет быть культурой и станет типом угодья${
      valueLabel ? ` «${valueLabel}»` : ''
    }. Запись пропадёт из справочника занятий — угодья ведутся в земельном банке, а не здесь.`;
  }
  return `${head}это перестанет быть одной культурой и станет смесью. Состав («Люцерна 60 % · Эспарцет 40 %») задаётся отдельным шагом — пока он пуст, смесь так и останется без состава.`;
}

export function NotACropDialog({
  item,
  open,
  onOpenChange,
  onSaved,
}: {
  item: CropQueueItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Пометка сохранена. Нужен вызывающему, чтобы закрыть за собой карточку:
   * закрывать её на ЛЮБОМ закрытии диалога нельзя — «Отмена» тогда убивала бы
   * и карточку, хотя человек ничего не менял.
   */
  onSaved?: () => void;
}) {
  const [kind, setKind] = useState<NotACropKind | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const markNotACrop = useMarkNotACrop();

  // Значения второго шага — из справочников с сервера, не из копии в коде.
  const fieldStates = useFieldStates();
  const landCategories = useLandCategories();
  const catalog = kind === 'field_state' ? fieldStates : kind === 'land_parcel' ? landCategories : null;
  const options: ReadonlyArray<CodeOption> = (catalog?.data ?? []).map((i) => ({
    code: i.code,
    label: i.name,
  }));

  // Сколько посевов стоит за записью — цифра ДО действия, а не после.
  // Спрашиваем сервер (а не `field_count` из списка): карточка справочника
  // может быть открыта на записи, список для которой загрузился минуту назад.
  const usage = useCropRecordUsage(open && item ? item.id : null);
  const sowings = usage.data?.sowings ?? item?.field_count ?? 0;

  // Новый термин — начинаем выбор с нуля (и подставляем уже сохранённое).
  useEffect(() => {
    if (!open) return;
    setKind(item?.not_a_crop_kind ?? null);
    setCode(item?.field_state_code ?? item?.land_category_code ?? null);
  }, [open, item]);

  const needsCode = needsCatalogCode(kind);
  const canSave = kind === 'crop_mix' || (needsCode && Boolean(code));
  const valueLabel = code ? (options.find((o) => o.code === code)?.label ?? null) : null;

  function close() {
    onOpenChange(false);
  }

  function handleSave() {
    if (!item || !kind) return;
    let payload: NotACropPayload;
    if (kind === 'crop_mix') payload = { kind: 'crop_mix' };
    else if (!code) return;
    else if (kind === 'field_state')
      payload = { kind: 'field_state', field_state_code: code };
    else payload = { kind: 'land_parcel', land_category_code: code };

    markNotACrop.mutate(
      { itemId: item.id, payload },
      {
        onSuccess: () => {
          toast.info('Термин размечен — это не культура');
          close();
          onSaved?.();
        },
        onError: (e) => toast.error(errMsg(e, 'Не удалось сохранить')),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : close())}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Что такое «{item?.local_name ?? '—'}»?</DialogTitle>
          <DialogDescription>
            Это не культура — уточните, что именно, чтобы система правильно
            считала площади и не подставляла это в посевы.
          </DialogDescription>
        </DialogHeader>

        {/* Шаг 1 — вид */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {KIND_TILES.map((t) => {
            const on = kind === t.kind;
            return (
              <button
                key={t.kind}
                type="button"
                aria-pressed={on}
                disabled={markNotACrop.isPending}
                onClick={() => {
                  setKind(t.kind);
                  setCode(null);
                }}
                className={cn(
                  'flex flex-col gap-1 rounded-xl border p-3 text-left transition-colors',
                  on
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/60',
                  markNotACrop.isPending && 'cursor-not-allowed opacity-60',
                )}
              >
                <span className="text-sm font-medium text-foreground">
                  {t.title}
                </span>
                <span className="text-xs text-muted-foreground">{t.hint}</span>
              </button>
            );
          })}
        </div>

        {/* Шаг 2 — конкретное значение */}
        {needsCode && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {kind === 'field_state'
                ? 'В каком состоянии поле?'
                : 'Какое это угодье?'}
            </p>
            {catalog?.isLoading ? (
              <Skeleton className="h-8 w-full" />
            ) : options.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Справочник сейчас недоступен — попробуйте позже или выберите
                «Смесь культур».
              </p>
            ) : (
            <div className="flex flex-wrap gap-2">
              {options.map((o) => {
                const on = code === o.code;
                return (
                  <button
                    key={o.code}
                    type="button"
                    aria-pressed={on}
                    disabled={markNotACrop.isPending}
                    onClick={() => setCode(o.code)}
                    className={cn(
                      'rounded-full border px-3 py-1 text-sm transition-colors',
                      on
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-foreground hover:bg-accent',
                    )}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
            )}
            {kind === 'field_state' && (
              <p className="text-xs text-muted-foreground">
                Состояние поля — про текущий сезон. Тип угодья («залежь»,
                «сенокос») выбирайте в соседней плитке.
              </p>
            )}
          </div>
        )}

        {/* Последствие — цифрами, ДО клика. Пустой счётчик тоже озвучен:
            «посевов нет» — это ответ, а не повод промолчать. */}
        {kind !== null && canSave && (
          <Alert data-testid="not-a-crop-consequence">
            <AlertTitle>Что произойдёт</AlertTitle>
            <AlertDescription className="space-y-2">
              <span className="block">
                {consequenceText(kind, valueLabel, sowings)}
              </span>
              {/* Граница — для ВСЕХ трёх видов, а не только для пара. */}
              <span className="block" data-testid="not-a-crop-summaries-limit">
                {SUMMARIES_LIMIT_TEXT}
              </span>
              <span className="block text-xs text-muted-foreground">
                Действие обратимо: пометку можно снять в карточке записи —
                кнопка «Вернуть в культуры».
              </span>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" disabled={markNotACrop.isPending} onClick={close}>
            Отмена
          </Button>
          <Button
            disabled={!canSave || markNotACrop.isPending}
            onClick={handleSave}
          >
            {markNotACrop.isPending && (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            )}
            {/* Подпись НЕ пляшет от фоновой загрузки счётчика: кнопка, которая
                называется то «Сохранить», то «Убрать из культур», заставляет
                перечитывать её каждый раз. Действие одно — так и называем. */}
            Убрать из культур
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default NotACropDialog;
